import {useQuery} from "@apollo/client";
import keyBy from "lodash/keyBy";
import {useEffect, useState} from "react";
import {useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {RequiredDateRange} from "@/components/datetime/utils.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GlobalAlertsResponse, GQL_GLOBAL_ALERT_STATES} from "@/lib/queries/alerting.ts";
import {ALERTS_FILTERS_STORAGE_KEY} from "@/pages/alerting/AlertsPage.utils.ts";
import {AlertsBulkActionsBar} from "@/pages/alerting/components/list/actions/AlertsBulkActionsBar.tsx";
import {AlertsControlBar} from "@/pages/alerting/components/list/alertsControlBar/AlertsControlBar.tsx";
import {AlertsSummaryBar} from "@/pages/alerting/components/list/AlertsSummaryBar.tsx";
import {AlertsTable} from "@/pages/alerting/components/list/AlertsTable.tsx";
import {IndexedAlertsContext} from "@/pages/alerting/components/list/IndexedAlertsContext.tsx";

const SHOW_DELETED_KEY = "show_deleted";

interface AlertListProps {
  visibleRange: RequiredDateRange | null;
}

export const AlertList = ({visibleRange}: AlertListProps) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const {
    search,
    searchDebounced,
    searchChanged,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortingString,
    filterSets,
    setFilterSets,
  } = useTable({defaultSort: "-up_at", sessionStorageKey: ALERTS_FILTERS_STORAGE_KEY});
  const urlShowDeleted = queryParams.get(SHOW_DELETED_KEY) || "";
  const [showDeleted, setShowDeleted] = useState(urlShowDeleted);

  // Back and links change the URL: follow it in the same render as the page and search, which sync the same way.
  useEffect(() => {
    setShowDeleted(urlShowDeleted);
  }, [urlShowDeleted]);

  const {data, loading, previousData} = useQuery<GlobalAlertsResponse>(GQL_GLOBAL_ALERT_STATES, {
    variables: {
      ...pagination,
      sort: sortingString,
      search: searchDebounced,
      show_deleted: showDeleted,
      filters: filterSets,
      visible_range: visibleRange,
    },
  });

  const showDeletedChanged = (showDeleted: string) => {
    const params = new URLSearchParams(queryParams);
    if (showDeleted.length === 0) {
      params.delete(SHOW_DELETED_KEY);
    } else {
      params.set(SHOW_DELETED_KEY, showDeleted);
    }
    setQueryParams(params);
    setShowDeleted(showDeleted);
  };

  const {user} = useCurrentUserContext();
  const renderData = loading ? previousData : data;

  const alertItems = renderData?.globalAlertStates.items ?? [];
  const {rowSelection, setRowSelection} = useFilteredRowSelection({
    data: alertItems,
    getRowId: (row) => String(row.id),
  });

  if (!renderData) {
    return <LoadingContainer />;
  }

  return (
    <IndexedAlertsContext.Provider value={keyBy(renderData.globalAlertStates.items, "id")}>
      <AlertsSummaryBar
        stats={renderData.globalAlertStates.stats}
        filterSets={filterSets}
        onFilterSetChange={setFilterSets}
      />
      <AlertsControlBar
        search={search}
        setSearch={searchChanged}
        showDeleted={showDeleted}
        setShowDeleted={showDeletedChanged}
        filterSets={filterSets}
        onFilterSetChange={setFilterSets}
      />
      <AlertsTable
        alerts={renderData.globalAlertStates.items}
        sortColumn={sorting.column}
        sortDirection={sorting.direction}
        onSortingChange={setSorting}
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={renderData.globalAlertStates.total}
        isFiltered={searchDebounced.trim().length > 0 || Object.keys(filterSets).length > 0}
        rowSelection={rowSelection}
        setRowSelection={user?.has_write_access ? setRowSelection : undefined}
        actionBar={
          user?.has_write_access && (
            <AlertsBulkActionsBar
              alerts={renderData.globalAlertStates.items}
              selectedItems={rowSelection}
              setSelectedItems={setRowSelection}
            />
          )
        }
      />
    </IndexedAlertsContext.Provider>
  );
};
